import React, { useState, useEffect } from 'react';
import { useAppContext } from '../AppContext';
import { collection, query, where, orderBy, limit, getDocs } from 'firebase/firestore';
import LoadingSpinner from '../components/LoadingSpinner';
import { Swords, Shield, TrendingUp, TrendingDown } from 'lucide-react';
import TimeAgo from 'react-timeago';

const Banner = ({ text, color }) => (
    <div className={`absolute top-0 right-0 text-xs font-bold text-white px-4 py-1 ${color} transform translate-x-1/2 -translate-y-1/2 rotate-45`}>
        {text}
    </div>
);

const MatchHistoryPage = () => {
    const { db, user } = useAppContext();
    const [matches, setMatches] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [flippedCardId, setFlippedCardId] = useState(null);

    useEffect(() => {
        if (!db || !user) {
            setLoading(false);
            return;
        }

        const fetchMatchHistory = async () => {
            try {
                const challengesRef = collection(db, 'artifacts/whispers-of-harmony/public/data/challenges');
                const q = query(challengesRef,
                    where('participants', 'array-contains', user.uid),
                    where('status', '==', 'completed'),
                    orderBy('createdAt', 'desc'),
                    limit(25)
                );
                const querySnapshot = await getDocs(q);
                const userMatches = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
                setMatches(userMatches);
            } catch (err) {
                setError(err);
            } finally {
                setLoading(false);
            }
        };

        fetchMatchHistory();
    }, [db, user]);

    if (loading) return <LoadingSpinner message="Fetching Match History..." />;
    if (error) return <p className="text-red-500 p-4">Error loading match history.</p>;

    return (
        <div className="w-full h-full bg-gradient-to-b from-slate-900/90 to-black/95 backdrop-blur-xl text-white p-4 md:p-8 flex flex-col overflow-hidden">
            <div className="text-center mb-10 mt-4">
                <h1 className="text-3xl md:text-4xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-purple-500 font-orbitron drop-shadow-sm">Match History</h1>
                <p className="text-gray-300 mt-2 font-light">Review your last 25 completed matches.</p>
            </div>
            <div className="max-w-4xl mx-auto space-y-4 w-full overflow-y-auto custom-scrollbar pb-20">
                {matches.map(match => {
                    const isWinner = match.winnerId === user.uid;
                    const opponent = user.uid === match.challengerId ? 
                        { name: match.challengedName, souls: match.challengedSouls } : 
                        { name: match.challengerName, souls: match.challengerSouls };
                    
                    return (
                        <div key={match.id} className={`flip-card ${flippedCardId === match.id ? 'flipped' : ''}`}>
                            <div className="flip-card-inner">
                                <div className="flip-card-front">
                                    <div className="relative bg-gray-800/40 backdrop-blur-md border border-white/10 rounded-xl p-5 flex items-center justify-between overflow-hidden h-full hover:bg-gray-800/60 transition-colors shadow-lg">
                                        {isWinner ? <Banner text="Victory" color="bg-green-600 shadow-lg shadow-green-900/50" /> : <Banner text="Defeat" color="bg-red-600 shadow-lg shadow-red-900/50" />}
                                        <div className="flex items-center gap-4">
                                            <div className={`p-3 rounded-full ${isWinner ? 'bg-green-500/10' : 'bg-red-500/10'}`}>
                                                {isWinner ? <TrendingUp className="text-green-400" size={32} /> : <TrendingDown className="text-red-400" size={32} />}
                                            </div>
                                            <div>
                                                <p className="text-xs text-gray-400 uppercase tracking-wider font-bold">VS {opponent.name}</p>
                                                <h3 className={`text-xl font-bold font-orbitron ${isWinner ? 'text-green-400' : 'text-red-400'}`}>
                                                    {isWinner ? 'VICTORY' : 'DEFEAT'}
                                                </h3>
                                            </div>
                                        </div>
                                        <div className="text-right mt-6 md:mt-0">
                                            <p className="text-xs text-gray-500 font-medium mb-2">
                                                {match.createdAt?.toDate ? <TimeAgo date={match.createdAt.toDate()} /> : 'Recently'}
                                            </p>
                                            <button onClick={() => setFlippedCardId(match.id)} className="px-4 py-2 bg-gray-700/50 hover:bg-gray-600/50 border border-white/10 rounded-lg font-bold text-xs btn-interactive transition-all backdrop-blur-sm">
                                                Details
                                            </button>
                                        </div>
                                    </div>
                                </div>
                                <div className="flip-card-back">
                                    <div className="bg-gray-900/90 backdrop-blur-xl border border-white/10 rounded-xl p-6 flex flex-col items-center justify-center h-full shadow-2xl">
                                        <h4 className="font-bold text-lg font-orbitron text-white mb-4">Match Details</h4>
                                        <div className="space-y-2 text-center mb-6">
                                            <p className="text-sm text-gray-300"><span className="text-gray-500 font-bold uppercase text-xs">Game:</span> <span className="font-bold text-white">{match.gameId || 'Unknown'}</span></p>
                                            <p className="text-sm text-gray-300"><span className="text-gray-500 font-bold uppercase text-xs">Soul Change:</span> <span className={`font-bold text-lg ml-2 ${isWinner ? 'text-green-400' : 'text-red-400'}`}>{isWinner ? '+' : '-'}{match.soulChange || 10}</span></p>
                                        </div>
                                        <button onClick={() => setFlippedCardId(null)} className="px-6 py-2 bg-gray-700 hover:bg-gray-600 rounded-lg font-bold text-xs btn-interactive transition-colors">
                                            Back
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

export default MatchHistoryPage;
